import { useCallback, useEffect, useMemo } from 'react';
import { useTriggerOverlay } from '../../handlers/useTriggerOverlay';
import { useCloseOverlay } from '../../overlay/OverlayContainer';
import { useGetPropertyDefinition } from 'crm-object-board-core/properties';
import { useObjectTypeDefinition, useObjectTypeId } from 'crm-object-board-core/objectTypeDefinition';
import useConditionalPropertiesPanel from 'customer-data-properties/panels/useConditionalPropertiesPanel';
import { useAllPropertiesWithValue } from './useAllPropertiesWithValue';
import { captureException, createBoardError } from '../../error/utils';
import emptyFunction from 'react-utils/emptyFunction';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const alwaysUndefined = emptyFunction.thatReturns(undefined);
const ConditionalPropertiesPanelWrapper = props => {
  const {
    objectId,
    columnId,
    originalColumnId,
    onApply,
    onCancel,
    propertyName
  } = props;
  const closeOverlay = useCloseOverlay();
  const typedef = useObjectTypeDefinition();
  const getPropertyDef = useGetPropertyDefinition();
  const {
    data: allPropertiesWithValues,
    loading,
    error
  } = useAllPropertiesWithValue(objectId);
  const stageDefinition = useMemo(() => {
    if (!typedef || !propertyName) {
      return null;
    }
    return getPropertyDef(propertyName);
  }, [typedef, getPropertyDef, propertyName]);
  const handleApply = useCallback(updates => {
    closeOverlay();
    onApply(updates);
  }, [closeOverlay, onApply]);
  const handleCancel = useCallback(() => {
    closeOverlay();
    onCancel();
  }, [closeOverlay, onCancel]);
  const objectTypeId = useObjectTypeId();
  const getSavedValue = useCallback(savedPropertyName => allPropertiesWithValues && allPropertiesWithValues[savedPropertyName] && allPropertiesWithValues[savedPropertyName].value, [allPropertiesWithValues]);
  const {
    loading: loadingConditionalProperties,
    conditionalPropertiesPanel,
    error: conditionaPropertiesError,
    maybeOpenConditionalPropertiesPanel
  } = useConditionalPropertiesPanel({
    objectTypeId,
    objectId,
    property: stageDefinition === null || stageDefinition === void 0 ? void 0 : stageDefinition.property,
    value: originalColumnId,
    onChange: handleApply,
    onCancel: handleCancel,
    getDraftValue: alwaysUndefined,
    getSavedValue
  });
  useEffect(() => {
    if (!loadingConditionalProperties && !loading && propertyName) {
      maybeOpenConditionalPropertiesPanel({
        [propertyName]: columnId
      });
    }
  }, [loadingConditionalProperties, loading, maybeOpenConditionalPropertiesPanel, propertyName, columnId]);

  // TODO: Proper loading and error states
  // https://git.hubteam.com/HubSpot/crm-index-ui/issues/2454

  if (loading || loadingConditionalProperties) {
    return null;
  }
  if (error || conditionaPropertiesError || !stageDefinition || !propertyName) {
    // CPP will error out if any of these are not available.
    // These could become undefined if API returns property definitions or pipelines for some reason
    if (conditionaPropertiesError) {
      //other captures are handled at the LINK level
      const boardError = createBoardError('Failed to retrieve data from the GetDataForConditionalPropertiesPanel query', conditionaPropertiesError);
      captureException(boardError, conditionaPropertiesError, {
        extra: {
          objectId,
          columnId
        },
        tags: {
          isConditionalPropertiesPanel: 'true'
        }
      });
    }
    handleCancel();
    return null;
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: conditionalPropertiesPanel
  });
};
export const useOpenConditionalPropertiesPanel = propertyName => {
  const trigger = useTriggerOverlay();
  return useCallback(options => {
    return new Promise(res => {
      const overlay = () => /*#__PURE__*/_jsx(ConditionalPropertiesPanelWrapper, Object.assign({}, options, {
        onApply: res,
        onCancel: () => res(false),
        propertyName: propertyName
      }));
      trigger(overlay);
    });
  }, [trigger, propertyName]);
};